import { queryUploadUrl4Scene, queryUploadUrl4CallBack } from './ubUploadHelper';
import { useAlert } from 'dashboard/composables';
import { reportFileUploadEvent } from './fileUploadEvents';

const canvas = document.createElement('canvas');
const context = canvas.getContext('2d');

export const imgCompressOption = {
  quality: 0.7,
  width: 720,
};

export const videoCompressOption = {
  quality: 0.7,
  width: 720,
};

/**
 * Compress image with specified parameters
 * @param {string} path - Image path/URL
 * @param {Object} obj - Compression parameters
 * @param {number} obj.width - Target width
 * @param {number} [obj.height] - Target height
 * @param {number} obj.quality - Compression quality (0-1)
 * @param {string} obj.type - Output file type
 * @returns {Promise<File>} - Compressed image file
 */
export const compressImg = (path, obj) => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      reject(new Error('Image compression timeout'));
    }, 10e4);

    try {
      const img = new Image();
      img.src = path;

      img.onload = function () {
        const { width, height } = img;
        const scale = obj.quality;
        canvas.width = width;
        canvas.height = height;
        
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        canvas.toBlob(
          (blob) => {
            const file = new window.File([blob], '', {
              type: obj.type,
              lastModified: Date.now(),
            });
            resolve(file);
          },
          obj.type,
          scale
        );
      };

      img.onerror = function () {
        reject(new Error('Failed to load image'));
      };
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Load and process image file
 * @param {Blob} file - Image file
 * @param {number} imgWidth - Target width
 * @returns {Promise<File>} - Processed image file
 */
export const imgLoader = async (file, imgWidth) => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      reject();
    }, 10e3);
    try {
      if (file.type === 'image/gif') {
        resolve(file);
        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        const img = await compressImg(reader.result, {
          quality: 0.7,
          type: file.type,
          width: imgWidth,
        });

        resolve(img);
      };
    } catch (error) {
      console.log('test upload error', error);
      reject();
    }
  });
};

/**
 * Generate video cover image
 * @param {File} file - Video file
 * @param {number} [seekTo=0] - Time point to capture cover
 * @returns {Promise<Blob>} - Video cover image blob
 */
export function getVideoCover(file, seekTo = 0) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.src = URL.createObjectURL(file);
    video.muted = true;
    video.playsInline = true;

    video.onloadedmetadata = () => {
      video.currentTime = seekTo;
    };

    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to generate thumbnail'));
        }
        URL.revokeObjectURL(video.src);
      }, 'image/png');
    };

    video.onerror = (error) => reject(error);
  });
} 

export const getUBAttachmentUploadResult = async (file) => {
  try {
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    const isPdf = file.type.startsWith('application/pdf');
    const getFileType = () => {
      if (isImage) return 'image';
      if (isVideo) return 'video';
      return 'file';
    }
    const type = getFileType();
    reportFileUploadEvent('startQueryUploadUrl4Scene', '');
    const uploadResult = await queryUploadUrl4Scene({ type, extension: file.name.split('.').pop() });
    reportFileUploadEvent('endQueryUploadUrl4Scene', '', { ...uploadResult.data });
    const { preSignedUrl, fileUrl } = uploadResult.data || {};
    let processedFile = file;
    let thumbUrl = null;
    let width = null;
    let height = null;

    if (isImage) {
      // Process image: compress and get dimensions
      reportFileUploadEvent('startImgLoader', '');
      processedFile = await imgLoader(file, imgCompressOption.width);
      reportFileUploadEvent('endImgLoader', '', { ...processedFile });
      const img = new Image();
      img.src = URL.createObjectURL(processedFile);
      await new Promise((resolve, reject) => {
        img.onload = () => {
          width = img.width;
          height = img.height;
          URL.revokeObjectURL(img.src);
          resolve();
        };
        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };
      });
    } else if (isVideo) {
      // Process video: get cover and dimensions
      reportFileUploadEvent('startGetVideoCover', '');
      const coverBlob = await getVideoCover(file);
      reportFileUploadEvent('endGetVideoCover', '', { ...coverBlob });
      const coverFile = new File([coverBlob], 'cover.png', { type: 'image/png' });
      reportFileUploadEvent('startQueryUploadUrl4Scene', '');
      const coverUploadResult = await queryUploadUrl4Scene({ type: 'image' });
      reportFileUploadEvent('endQueryUploadUrl4Scene', '', { ...coverUploadResult.data });
      await queryUploadUrl4CallBack(coverUploadResult.data.preSignedUrl, coverFile);
      thumbUrl = coverUploadResult.data.fileUrl;

      // Get video dimensions
      const video = document.createElement('video');
      video.src = URL.createObjectURL(file);
      await new Promise((resolve) => {
        video.onloadedmetadata = () => {
          width = video.videoWidth;
          height = video.videoHeight;
          URL.revokeObjectURL(video.src);
          resolve();
        };
      });
    } else if (isPdf) {
      processedFile = file;
    }

    // Upload the processed file
    await queryUploadUrl4CallBack(preSignedUrl, processedFile);
    reportFileUploadEvent('endQueryUploadUrl4CallBack', '', { ...processedFile });

    const ubAttachment = {
      data_url: fileUrl,
      file_type: type,
      extension: {
        width,
        height,
        file_name: file.name,
        type: file.type,
        ...(thumbUrl && { thumb_url: thumbUrl })
      }
    };
    reportFileUploadEvent('generateUBAttachment', '', { ...ubAttachment });
    return {
      success: true,
      ubAttachment,
    };
  } catch (error) {
    reportFileUploadEvent('getUBAttachmentUploadResultError', '', { ...error });
    console.log('test upload error', error);
    useAlert(error.message || "file upload failed, please try again");
    return {
      success: false,
      ubAttachment: null,
    };
  }
}